import { ProcessingLevel } from "@redotech/crdb-enums/fulfillment/processing-level";
import { InventoryItemKind } from "@redotech/crdb-enums/inventory/inventory-item-kind";
import { ReturnVerificationResult } from "@redotech/crdb-enums/returns/return-verification-result";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { locationIdFormat } from "@redotech/redo-inventory-id";
import {
  conditionLeaves,
  INVENTORY_RULE_FACTS,
  type InventoryRuleConditionLeaf,
  type InventoryRuleConditionNode,
  type InventoryRuleConditions,
  inventoryRuleCriteriaKey,
  type InventoryRuleFact,
  isPlainAnd,
  splitExclusions,
  withExclusions,
} from "./conditions";

/** Facts only the API writes: criteria naming them are refused. */
export const INVENTORY_RULE_SCOPE_FACTS: readonly InventoryRuleFact[] = [
  "location",
  "location_kind",
  "processing_level",
];

/** What a lane action can depend on in an earlier action of its step; MR 14 adds transform results. */
export const INVENTORY_RULE_BRANCH_RESULTS = [
  "applied",
  "not_applied",
] as const;
export type InventoryRuleBranchResult =
  (typeof INVENTORY_RULE_BRANCH_RESULTS)[number];

/** A lane action's dependency on the rule of an earlier action in its step. */
export interface InventoryRuleBranch {
  /** The earlier rule, as an `inr_` id. */
  ruleId: string;
  result: InventoryRuleBranchResult;
}

/** The conditions the API adds to a rule's criteria. */
export interface InventoryRuleScope {
  locationId: bigint;
  /** Set on lane rules, which apply at one processing level. */
  processingLevel: ProcessingLevel | null;
  /** Set on the day 0 rules of rows in `excludesUndeclaredAt`. */
  excludeUndeclared: boolean;
}

const EMPTY_CRITERIA: InventoryRuleConditions = {
  nodeType: "group",
  conjunctionMode: ConjunctionMode.AND,
  children: [],
};

function locationLeaf(locationId: bigint): InventoryRuleConditionLeaf {
  return {
    nodeType: "leaf",
    fact: "location",
    operator: "in",
    value: [locationIdFormat.write(locationId)],
  };
}

const RETURNS_CENTER_LEAF: InventoryRuleConditionLeaf = {
  nodeType: "leaf",
  fact: "location_kind",
  operator: "in",
  value: ["returns_center"],
};

function processingLevelLeaf(
  processingLevel: ProcessingLevel,
): InventoryRuleConditionLeaf {
  return {
    nodeType: "leaf",
    fact: "processing_level",
    operator: "in",
    value: [processingLevel],
  };
}

const NOT_UNDECLARED_LEAF: InventoryRuleConditionLeaf = {
  nodeType: "leaf",
  fact: "kind",
  operator: "not_in",
  value: [InventoryItemKind.Undeclared],
};

function branchLeaves(
  branch: InventoryRuleBranch,
): InventoryRuleConditionLeaf[] {
  switch (branch.result) {
    case "applied":
      return [appliedRulesLeaf("contains_any", branch.ruleId)];
    case "not_applied":
      return [appliedRulesLeaf("contains_none", branch.ruleId)];
  }
}

function appliedRulesLeaf(
  operator: "contains_any" | "contains_none",
  ruleId: string,
): InventoryRuleConditionLeaf {
  return { nodeType: "leaf", fact: "applied_rules", operator, value: [ruleId] };
}

/** The branch a top-level condition names, or null. */
function readBranchLeaf(
  leaf: InventoryRuleConditionLeaf,
): InventoryRuleBranch | null {
  const ruleId = singleValue(leaf);
  if (leaf.fact !== "applied_rules" || ruleId === null) {
    return null;
  }
  if (leaf.operator === "contains_any") {
    return { ruleId, result: "applied" };
  }
  if (leaf.operator === "contains_none") {
    return { ruleId, result: "not_applied" };
  }
  return null;
}

function topChildren(
  criteria: InventoryRuleConditions,
): InventoryRuleConditionNode[] {
  if (isPlainAnd(criteria)) {
    return criteria.children;
  }
  return [criteria];
}

/** `AND[scope, criteria, NOT each exclusion]`, as stored on a returns center rule. */
export function composeInventoryRuleConditions({
  criteria,
  scope,
  exclusions,
}: {
  criteria: InventoryRuleConditions;
  scope: InventoryRuleScope;
  exclusions: readonly InventoryRuleConditions[];
}): InventoryRuleConditions {
  const scopeLeaves = [
    locationLeaf(scope.locationId),
    RETURNS_CENTER_LEAF,
    ...(scope.processingLevel === null
      ? []
      : [processingLevelLeaf(scope.processingLevel)]),
    ...(scope.excludeUndeclared ? [NOT_UNDECLARED_LEAF] : []),
  ];
  return withExclusions(
    {
      nodeType: "group",
      conjunctionMode: ConjunctionMode.AND,
      children: [...scopeLeaves, ...topChildren(criteria)],
    },
    exclusions,
  );
}

export interface ReadInventoryRuleConditions {
  /** As the editor saved them, without anything the API added. */
  criteria: InventoryRuleConditions;
  exclusions: InventoryRuleConditions[];
  /** Null when the rule doesn't name exactly one location. */
  locationId: bigint | null;
  returnsCenterOnly: boolean;
  processingLevel: ProcessingLevel | null;
  excludesUndeclared: boolean;
  branch: InventoryRuleBranch | null;
}

/**
 * Whether a rule in run order branches on a later one of `rules`. The engine marks a rule applied as it
 * runs, and a lane reads a branch only after its earlier action, so such an order breaks the branch.
 */
export function branchesBeforeParent(
  rules: readonly { id: string; conditions: InventoryRuleConditions }[],
): boolean {
  const indexById = new Map(rules.map((rule, index) => [rule.id, index]));
  return rules.some((rule, index) => {
    const branch = readInventoryRuleConditions(rule.conditions).branch;
    return branch !== null && (indexById.get(branch.ruleId) ?? -1) > index;
  });
}

/** Adds a lane action's branch to its composed conditions, inside their exclusions. */
export function withBranchCondition(
  conditions: InventoryRuleConditions,
  branch: InventoryRuleBranch,
): InventoryRuleConditions {
  const { criteria, exclusions } = splitExclusions(conditions);
  return withExclusions(
    {
      nodeType: "group",
      conjunctionMode: ConjunctionMode.AND,
      children: [...topChildren(criteria), ...branchLeaves(branch)],
    },
    exclusions,
  );
}

function singleValue(leaf: InventoryRuleConditionLeaf): string | null {
  if (!Array.isArray(leaf.value) || leaf.value.length !== 1) {
    return null;
  }
  return leaf.value[0] ?? null;
}

function isLeaf(
  node: InventoryRuleConditionNode,
  fact: InventoryRuleFact,
  operator: InventoryRuleConditionLeaf["operator"],
): node is InventoryRuleConditionLeaf {
  return (
    node.nodeType === "leaf" && node.fact === fact && node.operator === operator
  );
}

function readLocationId(leaf: InventoryRuleConditionLeaf): bigint | null {
  const value = singleValue(leaf);
  if (value === null || !locationIdFormat.test(value)) {
    return null;
  }
  try {
    return locationIdFormat.read(value);
  } catch {
    return null;
  }
}

function readProcessingLevel(
  leaf: InventoryRuleConditionLeaf,
): ProcessingLevel | null {
  const value = singleValue(leaf);
  return (
    Object.values(ProcessingLevel).find((level) => level === value) ?? null
  );
}

/** A lone `grade in [name]`: a grade row's criteria. */
export function gradeRowName(criteria: InventoryRuleConditions): string | null {
  const [only, ...rest] = topChildren(criteria);
  if (only === undefined || rest.length > 0 || !isLeaf(only, "grade", "in")) {
    return null;
  }
  return singleValue(only);
}

export function gradeRowCriteria(gradeName: string): InventoryRuleConditions {
  return {
    nodeType: "group",
    conjunctionMode: ConjunctionMode.AND,
    children: [
      { nodeType: "leaf", fact: "grade", operator: "in", value: [gradeName] },
    ],
  };
}

export type InventoryLaneVerificationResult =
  | ReturnVerificationResult.Pass
  | ReturnVerificationResult.Fail;

export function verificationRowCriteria(
  result: InventoryLaneVerificationResult,
): InventoryRuleConditions {
  return {
    nodeType: "group",
    conjunctionMode: ConjunctionMode.AND,
    children: [
      {
        nodeType: "leaf",
        fact: "verification_result",
        operator: "in",
        value: [result],
      },
    ],
  };
}

export function additionalItemRowCriteria(
  identified: boolean,
): InventoryRuleConditions {
  return {
    nodeType: "group",
    conjunctionMode: ConjunctionMode.AND,
    children: [
      {
        nodeType: "leaf",
        fact: "kind",
        operator: "in",
        value: [InventoryItemKind.Undeclared],
      },
      {
        nodeType: "leaf",
        fact: "has_product",
        operator: "is",
        value: identified,
      },
    ],
  };
}

/** Receiving-level items take the receiving outcome setting, so rules run only at verification and grading. */
export function inventoryRulesRunAt(processingLevel: ProcessingLevel): boolean {
  return processingLevel !== ProcessingLevel.Receiving;
}

export function catchAllCriteria(): InventoryRuleConditions {
  return EMPTY_CRITERIA;
}

function sameCriteria(
  a: InventoryRuleConditions,
  b: InventoryRuleConditions,
): boolean {
  return inventoryRuleCriteriaKey(a) === inventoryRuleCriteriaKey(b);
}

/**
 * A grade or verification row at its level. Additional items have their
 * own rows, so these rows' day 0 rules leave undeclared items out.
 */
export function excludesUndeclaredAt(
  criteria: InventoryRuleConditions,
  processingLevel: ProcessingLevel,
): boolean {
  switch (processingLevel) {
    case ProcessingLevel.Grading:
      return gradeRowName(criteria) !== null;
    case ProcessingLevel.Verification:
      return (
        sameCriteria(
          criteria,
          verificationRowCriteria(ReturnVerificationResult.Pass),
        ) ||
        sameCriteria(
          criteria,
          verificationRowCriteria(ReturnVerificationResult.Fail),
        )
      );
    case ProcessingLevel.Receiving:
      return false;
  }
}

/** The verification result a verification row's criteria name, or null. */
export function verificationRowResult(
  criteria: InventoryRuleConditions,
): InventoryLaneVerificationResult | null {
  for (const result of [
    ReturnVerificationResult.Pass,
    ReturnVerificationResult.Fail,
  ] as const) {
    if (sameCriteria(criteria, verificationRowCriteria(result))) {
      return result;
    }
  }
  return null;
}

/** Whether an additional item row's criteria name identified items, or null for other criteria. */
export function additionalItemRowIdentified(
  criteria: InventoryRuleConditions,
): boolean | null {
  if (sameCriteria(criteria, additionalItemRowCriteria(true))) {
    return true;
  }
  if (sameCriteria(criteria, additionalItemRowCriteria(false))) {
    return false;
  }
  return null;
}

export function isCatchAllCriteria(criteria: InventoryRuleConditions): boolean {
  return sameCriteria(criteria, EMPTY_CRITERIA);
}

/** Undoes `composeInventoryRuleConditions`. */
export function readInventoryRuleConditions(
  conditions: InventoryRuleConditions,
): ReadInventoryRuleConditions {
  const { criteria: combined, exclusions } = splitExclusions(conditions);
  let locationId: bigint | null = null;
  let returnsCenterOnly = false;
  let processingLevel: ProcessingLevel | null = null;
  let undeclaredLeafIndex: number | null = null;
  let branch: InventoryRuleBranch | null = null;
  const rest: InventoryRuleConditionNode[] = [];
  for (const child of topChildren(combined)) {
    if (branch === null && child.nodeType === "leaf") {
      branch = readBranchLeaf(child);
      if (branch !== null) {
        continue;
      }
    }
    if (locationId === null && isLeaf(child, "location", "in")) {
      const id = readLocationId(child);
      if (id !== null) {
        locationId = id;
        continue;
      }
    }
    if (
      !returnsCenterOnly &&
      isLeaf(child, "location_kind", "in") &&
      singleValue(child) === "returns_center"
    ) {
      returnsCenterOnly = true;
      continue;
    }
    if (processingLevel === null && isLeaf(child, "processing_level", "in")) {
      const level = readProcessingLevel(child);
      if (level !== null) {
        processingLevel = level;
        continue;
      }
    }
    if (
      undeclaredLeafIndex === null &&
      isLeaf(child, "kind", "not_in") &&
      singleValue(child) === InventoryItemKind.Undeclared
    ) {
      undeclaredLeafIndex = rest.length;
    }
    rest.push(child);
  }
  // Only a fixed row's rules carry the undeclared condition for the API; elsewhere it is the editor's.
  const withoutUndeclared = rest.filter(
    (_child, index) => index !== undeclaredLeafIndex,
  );
  const excludesUndeclared =
    undeclaredLeafIndex !== null &&
    processingLevel !== null &&
    excludesUndeclaredAt(criteriaOf(withoutUndeclared), processingLevel);
  return {
    criteria: criteriaOf(excludesUndeclared ? withoutUndeclared : rest),
    exclusions,
    locationId,
    returnsCenterOnly,
    processingLevel,
    excludesUndeclared,
    branch,
  };
}

function criteriaOf(
  children: readonly InventoryRuleConditionNode[],
): InventoryRuleConditions {
  const [only] = children;
  if (children.length === 1 && only?.nodeType === "group") {
    return only;
  }
  return {
    nodeType: "group",
    conjunctionMode: ConjunctionMode.AND,
    children: [...children],
  };
}

/** Facts in `criteria` the editor may not name, because the API adds them. */
export function scopeFactsInCriteria(
  criteria: InventoryRuleConditions,
): InventoryRuleFact[] {
  return [
    ...new Set(
      conditionLeaves(criteria)
        .map((leaf) => leaf.fact)
        .filter((fact) => INVENTORY_RULE_SCOPE_FACTS.includes(fact)),
    ),
  ];
}

/**
 * False only when the criteria provably match no item in common: both require
 * a fact to hold values that can't coincide. Anything else may overlap.
 */
export function mayOverlap(
  a: InventoryRuleConditions,
  b: InventoryRuleConditions,
): boolean {
  const leavesA = topChildren(a).filter(
    (node): node is InventoryRuleConditionLeaf => node.nodeType === "leaf",
  );
  const leavesB = topChildren(b).filter(
    (node): node is InventoryRuleConditionLeaf => node.nodeType === "leaf",
  );
  return !leavesA.some((leafA) =>
    leavesB.some(
      (leafB) => leafA.fact === leafB.fact && contradicts(leafA, leafB),
    ),
  );
}

function normalizedValues(leaf: InventoryRuleConditionLeaf): Set<string> {
  const values = Array.isArray(leaf.value) ? leaf.value : [];
  return new Set(
    INVENTORY_RULE_FACTS[leaf.fact].caseInsensitive
      ? values.map((value) => value.toLowerCase())
      : values,
  );
}

/** Multi-valued facts match `in` on any one value, so two `in`s with distinct values can both hold. */
function isSingleValued(fact: InventoryRuleFact): boolean {
  const definition = INVENTORY_RULE_FACTS[fact];
  if (definition.type === "storage_location" || definition.type === "set") {
    return false;
  }
  if (definition.type === "enum") {
    return fact !== "storage_location_type";
  }
  return true;
}

function contradicts(
  a: InventoryRuleConditionLeaf,
  b: InventoryRuleConditionLeaf,
): boolean {
  const pair = (x: string, y: string) =>
    (a.operator === x && b.operator === y) ||
    (a.operator === y && b.operator === x);
  if (pair("is_empty", "is_not_empty")) {
    return true;
  }
  if (a.operator === "is" && b.operator === "is") {
    return a.value !== b.value;
  }
  if (!isSingleValued(a.fact)) {
    return false;
  }
  if (pair("is_empty", "in")) {
    return true;
  }
  if (a.operator === "in" && b.operator === "in") {
    const valuesB = normalizedValues(b);
    return ![...normalizedValues(a)].some((value) => valuesB.has(value));
  }
  if (pair("in", "not_in")) {
    const included = a.operator === "in" ? a : b;
    const excluded = normalizedValues(a.operator === "in" ? b : a);
    return [...normalizedValues(included)].every((value) =>
      excluded.has(value),
    );
  }
  return false;
}

/** Deep equality for stored JSON, whatever its key order. */
export function sameInventoryRuleJson(a: unknown, b: unknown): boolean {
  return stableJson(a) === stableJson(b);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJson).join(",")}]`;
  }
  if (value !== null && typeof value === "object") {
    return `{${Object.entries(value)
      .filter(([, entry]) => entry !== undefined)
      .sort(([keyA], [keyB]) => (keyA < keyB ? -1 : keyA > keyB ? 1 : 0))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}
